import posthog from 'posthog-js';
import { useCallback } from 'react';
import { analytics } from '../utils/analytics'; // Use performant analytics instead of proxy directly
import {
    debounceAnalytics,
    throttleAnalytics,
} from '../utils/analytics-performance';

export interface AnalyticsEventProperties {
    [key: string]: any;
}

export function useAnalytics() {
    // Track custom events using NON-BLOCKING analytics
    // Changed: Made this non-async to prevent blocking UI
    const trackEvent = useCallback(
        (eventName: string, properties?: AnalyticsEventProperties) => {
            if (typeof window !== 'undefined') {
                // Fire and forget - don't await
                analytics.capture(eventName, properties);
            }
        },
        []
    );

    // Identify user using NON-BLOCKING analytics
    // Changed: Made this non-async to prevent blocking UI
    const identifyUser = useCallback(
        (userId: string, properties?: AnalyticsEventProperties) => {
            if (typeof window !== 'undefined') {
                // Fire and forget - don't await
                analytics.identify(userId, properties);
                // Store in localStorage is synchronous and fast
                localStorage.setItem('pair_user_id', userId);
                if (properties?.email) {
                    localStorage.setItem('pair_user_email', properties.email);
                }
            }
        },
        []
    );

    // Set user properties
    const setUserProperties = useCallback(
        (properties: AnalyticsEventProperties) => {
            if (typeof window !== 'undefined' && posthog) {
                posthog.people?.set(properties);
            }
        },
        []
    );

    // Track pairing flow events
    const trackPairingEvent = useCallback(
        (step: string, properties?: AnalyticsEventProperties) => {
            trackEvent(`pairing_${step}`, {
                ...properties,
                pairing_flow: true,
            });
        },
        [trackEvent]
    );

    // Track authentication events
    const trackAuthEvent = useCallback(
        (
            action: string,
            method: string,
            properties?: AnalyticsEventProperties
        ) => {
            trackEvent(`auth_${action}`, {
                method,
                ...properties,
            });
        },
        [trackEvent]
    );

    // Track AI curation events
    const trackAIEvent = useCallback(
        (action: string, properties?: AnalyticsEventProperties) => {
            trackEvent(`ai_curation_${action}`, {
                ...properties,
                ai_feature: true,
            });
        },
        [trackEvent]
    );

    // Track channel/artcast events
    const trackChannelEvent = useCallback(
        (action: string, properties?: AnalyticsEventProperties) => {
            trackEvent(`channel_${action}`, {
                ...properties,
            });
        },
        [trackEvent]
    );

    // Track error events
    const trackError = useCallback(
        (error: string, context?: AnalyticsEventProperties) => {
            trackEvent('error_occurred', {
                error_message: error,
                ...context,
            });
        },
        [trackEvent]
    );

    // Track performance metrics
    const trackPerformance = useCallback(
        (
            metric: string,
            value: number,
            properties?: AnalyticsEventProperties
        ) => {
            trackEvent('performance_metric', {
                metric_name: metric,
                metric_value: value,
                ...properties,
            });
        },
        [trackEvent]
    );

    // Track engagement events (THROTTLED to prevent flooding)
    const trackEngagement = useCallback(
        throttleAnalytics(
            (action: string, properties?: AnalyticsEventProperties) => {
                trackEvent(action, {
                    ...properties,
                    engagement_event: true,
                });
            },
            1000
        ), // Max once per second
        [trackEvent]
    );

    // Reset user (for logout)
    const resetUser = useCallback(() => {
        if (typeof window !== 'undefined' && posthog) {
            posthog.reset();
            localStorage.removeItem('pair_user_id');
            localStorage.removeItem('pair_user_email');
        }
    }, []);

    // Check if feature flag is enabled
    const isFeatureEnabled = useCallback((flagName: string): boolean => {
        if (typeof window !== 'undefined' && posthog) {
            return posthog.isFeatureEnabled(flagName) || false;
        }
        return false;
    }, []);

    return {
        trackEvent,
        identifyUser,
        setUserProperties,
        trackPairingEvent,
        trackAuthEvent,
        trackAIEvent,
        trackChannelEvent,
        trackError,
        trackPerformance,
        trackEngagement,
        resetUser,
        isFeatureEnabled,
    };
}
